/**
 * Root Layout
 * Responsive dashboard with collapsible sidebar
 */

import { useEffect, useRef } from 'react'
import { createRootRouteWithContext, Outlet, useLocation, useSelector, useDispatch, useNavigate, initializeToastService } from '@spcc/core'
import { ThemeProvider, I18nProvider, DataProvider, StoreProvider, CopilotProvider, SidebarProvider, DrawerProvider, useSidebar } from '@/providers'
import { Toaster, toast } from '@spcc/uikit'
import { DashboardHeader } from '@/components/header'
import { DashboardSidebar } from '@/components/sidebar'
import { ErrorBoundary } from '@/components/error-boundary'
import { MobileExportPreviewDrawer, MobileInvoicePreviewDrawer } from '@spcc/portal-shared'
import { cn } from '@/lib/utils'
import i18n from '@/lib/i18n'
import type { RouterContext } from '@/main'
import type { ToastOptions } from '@spcc/core'
import { isNativeApp } from '@/lib/platform'

// Analytics consent
import {
  ConsentBanner,
  selectConsentState,
  consentActions,
} from '@spcc/domains/analytics'

// Auth
import {
  authActions,
  selectIsAuthenticated,
  ProtectedRoute,
  SessionTimeoutProvider,
} from '@spcc/domains/auth'
import { GlobalSearchDialog } from '@spcc/domains/global-search'
import { selectLocationContext, selectLocationDerivedFactoryContext } from '@spcc/domains/locations'

import { PRIVACY_POLICY_VERSION } from '@/lib/policy'

function resolveToastText(value?: string): string | undefined {
  if (!value) return value
  if (!i18n.exists(value)) return value
  return i18n.t(value)
}

function createTranslatedToast(
  toastFn: (options: ToastOptions) => void
): (options: ToastOptions) => void {
  return (options: ToastOptions) => {
    const translatedOptions: ToastOptions = {
      ...options,
      title: resolveToastText(options.title),
      description: resolveToastText(options.description),
    }
    toastFn(translatedOptions)
  }
}

// Initialize toast service for saga usage
initializeToastService(createTranslatedToast(toast as unknown as (options: ToastOptions) => void))

export const Route = createRootRouteWithContext<RouterContext>()({
  component: RootLayout,
})

const AUTH_ROUTES = ['/login', '/register', '/forgot-password', '/reset-password', '/privacy-policy', '/account-deletion', '/sso/handoff', '/sso/handoff/']
const FACTORY_FILTER_SEARCH_ROUTES = new Set([
  '/orders',
  '/sales-orders',
  '/finance/transactions',
  '/finance/statement',
  '/finance/payments',
])

/**
 * Consent Banner Container
 * Wires up the dumb ConsentBanner with Redux
 */
function ConsentBannerContainer() {
  const dispatch = useDispatch()
  const consentState = useSelector(selectConsentState)

  return (
    <ConsentBanner
      consent={consentState}
      showBanner={consentState.showBanner}
      settingsOpen={consentState.settingsOpen}
      onAcceptAll={() => dispatch(consentActions.acceptAll({ policyVersion: PRIVACY_POLICY_VERSION }))}
      onRejectNonEssential={() => dispatch(consentActions.rejectNonEssential({ policyVersion: PRIVACY_POLICY_VERSION }))}
      onOpenSettings={() => dispatch(consentActions.openSettings())}
      onCloseSettings={() => dispatch(consentActions.closeSettings())}
      onUpdateConsent={(category, granted) =>
        dispatch(consentActions.updateConsent({ category, granted, policyVersion: PRIVACY_POLICY_VERSION }))
      }
      onSavePreferences={() => dispatch(consentActions.savePreferences({ policyVersion: PRIVACY_POLICY_VERSION }))}
    />
  )
}

function shouldRenderConsentBanner(): boolean {
  return !isNativeApp()
}

function shouldClearFactoryFilterSearch(pathname: string): boolean {
  return FACTORY_FILTER_SEARCH_ROUTES.has(pathname)
}

function FactoryFilterResetController() {
  const location = useLocation()
  const navigate = useNavigate()
  const currentFactoryId = useSelector(selectLocationDerivedFactoryContext)
  const previousFactoryIdRef = useRef<string | null | undefined>(undefined)

  useEffect(() => {
    if (previousFactoryIdRef.current === undefined) {
      previousFactoryIdRef.current = currentFactoryId
      return
    }

    if (previousFactoryIdRef.current === currentFactoryId) return

    previousFactoryIdRef.current = currentFactoryId

    if (!location.search || !shouldClearFactoryFilterSearch(location.pathname)) return

    void navigate({ search: {} as any, replace: true })
  }, [currentFactoryId, location.pathname, location.search, navigate])

  return null
}

function DashboardLayout() {
  const { isMobile } = useSidebar()
  const currentLocationId = useSelector(selectLocationContext)
  const currentFactoryId = useSelector(selectLocationDerivedFactoryContext)
  const contextKey = `${currentLocationId ?? 'all-locations'}:${currentFactoryId ?? 'all-factories'}`

  return (
    <DataProvider>
      <DrawerProvider>
        <div className="relative flex h-screen overflow-hidden">
          {/* Background gradients */}
          <div className="fixed inset-0 bg-gradient-to-br from-blue-50 via-slate-50 to-indigo-50 dark:from-slate-950 dark:via-slate-900 dark:to-blue-950/50" />
          <div className="fixed top-20 start-20 w-72 h-72 bg-primary/20 dark:bg-primary/10 rounded-full blur-3xl animate-pulse" />
          <div className="fixed bottom-20 end-20 w-96 h-96 bg-blue-400/15 dark:bg-blue-500/8 rounded-full blur-3xl animate-pulse delay-1000" />
          <div className="fixed top-1/2 start-1/3 w-64 h-64 bg-accent/10 dark:bg-accent/5 rounded-full blur-3xl animate-pulse delay-500" />

          {/* Content */}
          <div className="relative flex w-full z-10">
            <DashboardSidebar />
            <div className="flex-1 flex flex-col overflow-hidden min-w-0">
              <div className="safe-top">
                <DashboardHeader />
              </div>
              <main
                className={cn(
                  'flex-1 min-w-0 overflow-y-auto overflow-x-hidden',
                  isMobile ? 'p-4' : 'p-8'
                )}
              >
                <Outlet key={contextKey} />
              </main>
            </div>
          </div>
        </div>
      </DrawerProvider>
    </DataProvider>
  )
}

/**
 * Auth Initializer
 * Dispatches auth initialization on mount
 */
function AuthInitializer({ children }: { children: React.ReactNode }) {
  const dispatch = useDispatch()

  useEffect(() => {
    dispatch(authActions.initializeAuth())

    const handlePageShow = (event: PageTransitionEvent) => {
      if (event.persisted) {
        dispatch(authActions.initializeAuth())
      }
    }
    window.addEventListener('pageshow', handlePageShow)
    return () => window.removeEventListener('pageshow', handlePageShow)
  }, [dispatch])

  return <>{children}</>
}

function getErrorBoundaryResetKey(pathname: string, isAuthenticated: boolean): string {
  return `${pathname}:${isAuthenticated ? 'authenticated' : 'guest'}`
}

function RootShell() {
  const location = useLocation()
  const isAuthRoute =
    AUTH_ROUTES.includes(location.pathname) ||
    location.pathname.startsWith('/d/') ||
    location.pathname.startsWith('/dn/')
  const isAuthenticated = useSelector(selectIsAuthenticated)
  const renderConsentBanner = shouldRenderConsentBanner()
  const resetKey = getErrorBoundaryResetKey(location.pathname, isAuthenticated)

  return (
    <ErrorBoundary resetKey={resetKey}>
      <AuthInitializer>
        <CopilotProvider>
          <ThemeProvider>
            <I18nProvider>
              <SidebarProvider>
                {isAuthRoute ? (
                  // Auth pages - no dashboard layout
                  <Outlet />
                ) : (
                  // Protected dashboard layout
                  <ProtectedRoute>
                    <SessionTimeoutProvider timeoutMs={30 * 60 * 1000}>
                      <FactoryFilterResetController />
                      <DashboardLayout />
                    </SessionTimeoutProvider>
                  </ProtectedRoute>
                )}
                {/* Global Search Dialog */}
                <GlobalSearchDialog />
                {/* GDPR Consent Banner */}
                {renderConsentBanner && <ConsentBannerContainer />}
                {/* Toast notifications */}
                <Toaster />
                {/* Native mobile export preview/share */}
                <MobileExportPreviewDrawer />
                <MobileInvoicePreviewDrawer />
              </SidebarProvider>
            </I18nProvider>
          </ThemeProvider>
        </CopilotProvider>
      </AuthInitializer>
    </ErrorBoundary>
  )
}

function RootLayout() {
  return (
    <StoreProvider>
      <RootShell />
    </StoreProvider>
  )
}
